import { AgentApiResponse } from "@components/ChatBot/types";
import { Api } from "src/utils/api";
import { ChatStrategy, ChatStrategyConfig } from "./ChatStrategy";
import { v4 as uuidv4 } from "uuid";

export interface SimplifiedChatData {
  requestType: "analytics" | "support" | "feedback" | "custom";
  userInput?: any;
  metadata?: Record<string, any>;
}

export class SimplifiedChatStrategy extends Api implements ChatStrategy {
  private config: ChatStrategyConfig;
  private readonly sessionId: string;

  constructor(config: ChatStrategyConfig) {
    super(config.baseUrl, config.headers);
    this.config = config;
    this.sessionId = this.getOrCreateSessionId();
    this.axiosInstance.defaults.withCredentials = false;
  }

  private getOrCreateSessionId(): string {
    const STORAGE_KEY = "simplified_chat_session_id";
    let sessionId = sessionStorage.getItem(STORAGE_KEY);
    if (!sessionId) {
      sessionId = uuidv4();
      sessionStorage.setItem(STORAGE_KEY, sessionId);
    }
    return sessionId;
  }

  async sendMessage(
    message: string,
    additionalData?: SimplifiedChatData,
  ): Promise<AgentApiResponse> {
    const payload = {
      message,
      requestType: additionalData?.requestType ?? "custom",
      userInput: additionalData?.userInput,
      sessionId: this.sessionId,
      metadata: {
        timestamp: new Date().toISOString(),
        userId: this.getCurrentUserId(),
        ...additionalData?.metadata,
      },
    };

    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        const response = await this.post<any>("/chat", payload);
        return this.transformResponse(response, message, additionalData);
      } catch (error) {
        console.error(`N8n request failed (attempt ${attempt}):`, error);
        if (attempt === 2) {
          return this.createErrorResponse(message, error);
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }

    return this.createErrorResponse(message, new Error("Unexpected error"));
  }

  private transformResponse(
    serverResponse: any,
    _originalMessage: string,
    data?: SimplifiedChatData,
  ): AgentApiResponse {
    const responseMessage =
      serverResponse.output ??
      serverResponse.message ??
      serverResponse.data?.message ??
      serverResponse.data?.output;

    if (!responseMessage) {
      console.error("No valid response from N8n webhook:", serverResponse);
      throw new Error("Invalid server response format");
    }

    return {
      success: true,
      tools: [],
      finalMessage: responseMessage,
      role: "assistant",
      id: uuidv4(),
      buttons: this.generateButtons(data?.requestType),
    };
  }

  private createErrorResponse(message: string, error?: any): AgentApiResponse {
    let errorMessage =
      "Sorry, I'm unable to process your request right now. Please try again later.";

    if (error?.message === "Invalid server response format") {
      errorMessage =
        "I received an unexpected response format from the server. Please try again or contact support.";
    } else if (
      error?.name === "NetworkError" ||
      error?.code === "NETWORK_ERROR"
    ) {
      errorMessage =
        "I'm having trouble connecting to the server. Please check your connection and try again.";
    } else if (error?.response?.status) {
      errorMessage = `Server error (${error.response.status}). Please try again in a moment.`;
    } else {
      console.log("error");
    }

    console.error("Chat error details:", {
      originalMessage: message,
      error,
      errorMessage,
    });

    return {
      success: false,
      tools: [],
      finalMessage: errorMessage,
      role: "assistant",
      id: uuidv4(),
      buttons: null,
    };
  }

  private getCurrentUserId(): string {
    const token = localStorage.getItem("access_token");
    if (!token) {
      return "anonymous";
    }

    const parts = token.split(".");
    if (parts.length < 2) {
      console.warn("Invalid token format");
      return "anonymous";
    }

    try {
      const payload = JSON.parse(atob(parts[1])) as {
        sub?: string;
        user_id?: string;
      };
      return payload.sub ?? payload.user_id ?? "anonymous";
    } catch (err) {
      console.error("Error decoding token payload:", err);
      return "anonymous";
    }
  }

  private generateButtons(
    _requestType?: string,
  ): Array<Array<{ id: string; question: string; label: string }>> | null {
    return null;
  }

  getName(): string {
    return "Simplified Chat";
  }

  getDescription(): string {
    return "Simple chatbot for basic interactions and data collection";
  }

  updateConfig(newConfig: Partial<ChatStrategyConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}
